<script setup>
import { computed } from 'vue'

const props = defineProps({
  id: { type: String, default: null },
  size: { type: String, default: null },
  styleType: { type: String, default: null }, // option_style 投票选项样式：1-文字（默认），2-图片，3-图片+文字
  img: { type: [Object, String], default: null },
  coverRatio: { type: [Number, String], default: null }, // option_image_size 投票选项图片宽高比例：1-1:1, 2-4:3, 3-9:16, 4-16:9
  alt: { type: String, default: '' },
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  desc: { type: String, default: '' },
  desc2: { type: String, default: '' },
  linkText: { type: String, default: '' },
  link: { type: String, default: '' },
  titleTag: { type: String, default: 'h3' },
  backgroundc: { type: String, default: '#ffffff0f' },
  onlyShow: { type: Boolean, default: true }, // 只展示
  selected: { type: Boolean, default: false }, // 已选
  selectedText: { type: String, default: '已選' }
})

const emit = defineEmits(['select', 'imgclick'])

const isMobile = computed(() => ['s', 'xs'].includes(props.size))
const isLink = computed(() => props.link && props.linkText)
const cardMode = computed(() => {
  const { img, subtitle, desc, desc2, title, onlyShow } = props
  if (img) {
    if (!subtitle && !desc && !desc2 && !title && !isLink.value && onlyShow) return 'only-image'
    if (!subtitle && !desc && !desc2 && !title) return 'image'
    return 'image-text'
  } else {
    if (!subtitle && !desc && !desc2 && !isLink.value && onlyShow) return 'only-title'
    if (!subtitle && !desc && !desc2) return 'title'
    return 'text'
  }
})

const onSelect = () => {
  emit('select', props.id)
}
</script>

<template>
  <div
    class="tvb-vote-card"
    :class="[
      size ? 'card-' + size : '',
      'card-' + cardMode,
      { 'disable-hover': isMobile || onlyShow || selected },
      { 'card-selected': selected }
    ]"
  >
    <div class="tvb-vote-card-wrap tvb-hairline--surround">
      <div class="content-space">
        <div
          v-if="img"
          class="content-space tvb-vote-card__image tvb-hairline--surround"
          :class="coverRatio && 'cover-ratio-' + coverRatio"
          @click="emit('imgclick')"
        >
          <div class="tvb-vote-card__image--wrap">
            <tvb-image
              class="tvb-vote-card__cover"
              :src="img"
              :alt="alt"
              :transform="isMobile ? false : true"
              :backgroundc="backgroundc"
            ></tvb-image>
          </div>
        </div>

        <div v-if="subtitle || desc || title || desc2" class="content-space tvb-vote-card__info">
          <component v-if="title" :is="titleTag" class="clear-h info-space tvb-vote-card__info--tit"
            >{{ title
            }}<span v-show="cardMode === 'image-text' ? !isMobile : false" class="subtitle">{{
              subtitle ? ' ' + subtitle : ''
            }}</span></component
          >
          <div
            v-if="subtitle"
            v-show="cardMode === 'image-text' ? isMobile : true"
            class="info-space tvb-vote-card__info--subtitle"
          >
            {{ subtitle }}
          </div>
          <div v-if="desc" class="info-space tvb-vote-card__info--desc">{{ desc }}</div>
          <div v-if="desc2" class="info-space tvb-vote-card__info--desc">{{ desc2 }}</div>
        </div>
      </div>

      <div v-if="isLink || !onlyShow" class="content-space tvb-vote-card__action">
        <button
          v-if="isLink"
          class="tvb-vote-card__action--link tvb-hairline--surround"
          :class="{ 'btn-hover': !isMobile }"
        >
          <tvb-router-link :to="link" :linkRel="2" :linkTarget="2">
            {{ linkText }}
          </tvb-router-link>
        </button>
        <button
          v-if="!onlyShow"
          class="tvb-vote-card__action--select"
          :class="{ 'btn-hover': !isMobile && !selected }"
          @click="onSelect"
        >
          {{ selected ? selectedText : '投票' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tvb-vote-card {
  --vc-border-radius: 12px;
  --vc-padding: 16px;
  --vc-title-font-size: 18px;
  --vc-title-line-height: 26px;
  --vc-title-font-color: rgba(255, 255, 255, 0.9);
  --vc-title-font-weight: 500;
  --vc-title-letter-spacing: 0.5px;
  --vc-subtitle-font-size: 16px;
  --vc-subtitle-line-height: 24px;
  --vc-subtitle-font-color: rgba(255, 255, 255, 0.8);
  --vc-subtitle-letter-spacing: 0.5px;
  --vc-desc-font-size: 14px;
  --vc-desc-line-height: 20px;
  --vc-desc-font-color: rgba(255, 255, 255, 0.7);
  --vc-desc-letter-spacing: 0.3px;
  --vc-button-bgc: transparent;
  --vc-img-border-radius: 8px;
  --vc-cover-radio: 56.25%; // 如何计算值？ 9 / 16（高除以宽）
  --vc-gap: 16px;
  --vc-info-gap: 4px;
  --vc-btn-gap: 10px;
  --vc-title-bottom-gap: 0px;

  a {
    text-decoration: none;
    background-color: transparent;
    color: inherit;
    cursor: pointer;
    &:visited {
      color: inherit;
    }
  }

  .tvb-vote-card-wrap {
    height: 100%;
    position: relative;
    background-color: var(--tvb-vote-bgc);
    border-radius: var(--vc-border-radius);
    --tvb-hairline-border-radius: calc(2 * var(--vc-border-radius));
    --tvb-hairline-border-color: var(--tvb-vote-border-color);
    box-sizing: border-box;
    padding: var(--vc-padding);
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .content-space + .content-space {
      margin-top: var(--vc-gap);
    }
    .info-space + .info-space {
      margin-top: var(--vc-info-gap);
    }
    .tvb-vote-card__action {
      button + button {
        margin-top: var(--vc-btn-gap);
      }
    }
  }

  // img样式
  .tvb-vote-card__image {
    position: relative;
    border-radius: var(--vc-img-border-radius);
    --tvb-hairline-border-radius: calc(2 * var(--vc-img-border-radius));
    --tvb-hairline-border-color: var(--tvb-vote-border-color);
    &.cover-ratio-1 {
      --vc-cover-radio: calc((1 / 1) * 100%); // 1:1
    }
    &.cover-ratio-2 {
      --vc-cover-radio: calc((4 / 3) * 100%); // 4:3
    }
    &.cover-ratio-3 {
      --vc-cover-radio: calc((16 / 9) * 100%); // 9:16
    }
    &.cover-ratio-4 {
      --vc-cover-radio: calc((9 / 16) * 100%); // 16:9
    }
  }
  .tvb-vote-card__image--wrap {
    padding-top: var(--vc-cover-radio);
    border-radius: inherit;
    cursor: pointer;
  }
  .tvb-vote-card__cover {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: inherit;
    overflow: hidden;
    @include safari-overflow-hidden();
  }

  .tvb-vote-card__info--tit {
    font-weight: var(--vc-title-font-weight);
    font-size: var(--vc-title-font-size);
    color: var(--vc-title-font-color);
    line-height: var(--vc-title-line-height);
    letter-spacing: var(--vc-title-letter-spacing);
    margin-bottom: var(--vc-title-bottom-gap);
    word-break: break-all;
  }

  .tvb-vote-card__info--subtitle {
    font-size: var(--vc-subtitle-font-size);
    color: var(--vc-subtitle-font-color);
    line-height: var(--vc-subtitle-line-height);
    letter-spacing: var(--vc-subtitle-letter-spacing);
    word-break: break-all;
  }

  .tvb-vote-card__info--desc {
    font-size: var(--vc-desc-font-size);
    color: var(--vc-desc-font-color);
    line-height: var(--vc-desc-line-height);
    letter-spacing: var(--vc-desc-letter-spacing);
    word-break: break-all;
  }

  // button
  .tvb-vote-card__action--link,
  .tvb-vote-card__action--select {
    --tvb-hairline-border-width: 0px;
    --tvb-hairline-border-radius: calc(2 * 6px);
    border: none;
    outline: 0;
    cursor: pointer;
    background-color: var(--vc-button-bgc);
    border-radius: 6px;
    font-size: 15px;
    color: rgba(255, 255, 255, 0.8);
    line-height: 22px;
    box-sizing: border-box;
    padding: 8px 0;
    letter-spacing: 0.4px;
    width: 100%;
    transition:
      color 0.25s,
      background-color 0.25s;

    &.btn-hover:hover {
      --vc-button-bgc: #fff;
      color: #333;
    }
  }
  .tvb-vote-card__action--link {
    --tvb-hairline-border-width: 2px;
    --tvb-hairline-border-color: rgba(255, 255, 255, 0.2);
    a {
      display: block;
    }
  }
  .tvb-vote-card__action--select {
    --vc-button-bgc: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.9);
  }

  &.card-only-image {
    --vc-padding: 0;
  }

  &.card-image-text,
  &.card-image {
    --vc-padding: 12px;
    --vc-title-font-size: 16px;
    --vc-title-line-height: 24px;
    // --vc-subtitle-font-size: 14px;
    // --vc-subtitle-line-height: 20px;
    --vc-subtitle-font-color: var(--vc-title-font-color);
    --vc-subtitle-letter-spacing: 0.3px;
    --vc-gap: 12px;
    --vc-info-gap: 2px;
    .tvb-vote-card-wrap {
      .tvb-vote-card__info--tit {
        margin-bottom: 0;
      }
      .tvb-vote-card__info {
        text-align: center;
      }
    }
  }

  &.card-text {
    --vc-title-bottom-gap: 8px;
  }

  // hover
  &:not(.disable-hover) {
    cursor: pointer;
    .tvb-hairline--surround::after {
      transition: border 0.15s linear;
    }
    .tvb-vote-card-wrap:hover {
      --tvb-hairline-border-width: 8px;
      --tvb-hairline-border-color: #fff;
    }
  }

  // selected
  &.card-selected {
    .tvb-vote-card-wrap {
      --tvb-hairline-border-width: 8px;
      --tvb-hairline-border-color: #fff;
    }

    .tvb-vote-card__action--select {
      --vc-button-bgc: #fff;
      color: #333;
      // pointer-events: none;
    }
  }

  // mobile
  &.card-s,
  &.card-xs {
    --vc-border-radius: 10px;
    --vc-padding: 10px 12px 12px;
    --vc-title-font-size: 15px;
    --vc-title-line-height: 24px;
    --vc-title-font-weight: 400;
    --vc-title-letter-spacing: 0.3px;
    --vc-subtitle-font-size: 13px;
    --vc-subtitle-line-height: 18px;
    --vc-subtitle-letter-spacing: 0;
    --vc-desc-font-size: 13px;
    --vc-desc-line-height: 18px;
    --vc-desc-letter-spacing: 0;
    --vc-img-border-radius: 5px;
    --vc-gap: 12px;
    --vc-info-gap: 4px;
    --vc-btn-gap: 8px;

    .tvb-vote-card__action--link,
    .tvb-vote-card__action--select {
      --tvb-hairline-border-radius: calc(2 * 5px);
      border-radius: 5px;
      font-size: 13px;
      line-height: 18px;
      padding: 7px 0;
      letter-spacing: 0;
    }

    &.card-image-text,
    &.card-image {
      --vc-padding: 6px;
      --vc-title-font-size: 14px;
      --vc-title-line-height: 20px;
      --vc-subtitle-font-size: var(--vc-title-font-size);
      --vc-subtitle-line-height: var(--vc-title-line-height);
      --vc-subtitle-letter-spacing: 0.3px;
      --vc-gap: 6px;
      --vc-btn-gap: 6px;
      --vc-info-gap: 2px;
      .tvb-vote-card__info--subtitle {
        margin-top: 0;
      }
    }

    &.card-text {
      --vc-title-bottom-gap: 0px;
    }

    // selected
    &.card-selected {
      .tvb-vote-card-wrap {
        --tvb-hairline-border-width: 4px;
      }
    }
  }
}
</style>
